import React, { useState, useEffect } from 'react';
import {
    Box, Paper, Typography, TextField, Button, Grid, CircularProgress
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { completeGoogleRegistration } from '../../services/auth';
import { useApp } from '../../context/AppContext';

// Busca endereço pelo CEP usando a API pública ViaCEP (sem necessidade de chave).
async function fetchCep(cep) {
    const onlyDigits = String(cep).replace(/\D/g, '');
    if (onlyDigits.length !== 8) return null;
    const res = await fetch(`https://viacep.com.br/ws/${onlyDigits}/json/`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.erro) return null;
    return data;
}

export default function CompleteRegistration() {
    const navigate = useNavigate();
    const { login: doLogin, setProfile } = useApp();
    const [params] = useSearchParams();
    const token = params.get('token');

    const [fullName, setFullName] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [cep, setCep] = useState('');
    const [logradouro, setLogradouro] = useState('');
    const [numero, setNumero] = useState('');
    const [complemento, setComplemento] = useState('');
    const [bairro, setBairro] = useState('');
    const [cidade, setCidade] = useState('');
    const [estado, setEstado] = useState('');
    const [loadingCep, setLoadingCep] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!token) {
            toast.error('Token de conclusão ausente');
            navigate('/login');
        }
    }, [token, navigate]);

    const handleCepChange = async (e) => {
        const value = e.target.value;
        setCep(value);
        const digits = value.replace(/\D/g, '');
        if (digits.length === 8) {
            setLoadingCep(true);
            try {
                const data = await fetchCep(digits);
                if (data) {
                    setLogradouro(data.logradouro || '');
                    setBairro(data.bairro || '');
                    setCidade(data.localidade || '');
                    setEstado(data.uf || '');
                } else {
                    toast.error('CEP não encontrado');
                }
            } catch (err) {
                toast.error('Erro ao buscar CEP');
            } finally {
                setLoadingCep(false);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!fullName.trim()) return toast.error('Nome completo é obrigatório');
        if (!birthDate) return toast.error('Data de nascimento é obrigatória');
        if (!cep.trim()) return toast.error('CEP é obrigatório');
        if (!numero.trim()) return toast.error('Número é obrigatório');

        setLoading(true);
        try {
            const res = await completeGoogleRegistration(token, {
                fullName: fullName.trim(),
                birthDate,
                address: {
                    cep: cep.trim(),
                    logradouro: logradouro.trim(),
                    numero: numero.trim(),
                    complemento: complemento.trim(),
                    bairro: bairro.trim(),
                    cidade: cidade.trim(),
                    estado: estado.trim()
                }
            });
            localStorage.setItem('ag_token', res.token);
            if (res.user) setProfile(res.user);
            doLogin();
            toast.success('Cadastro concluído!');
            navigate('/dashboard');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Erro ao concluir cadastro');
        } finally { setLoading(false); }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
            <Paper sx={{ p: 4, width: 480 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                        component="img"
                        src="/logo.png"
                        alt="AutoGrow"
                        sx={{ width: 40, height: 40, mr: 1 }}
                    />
                    <Typography variant="h5">Concluir cadastro</Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Para finalizar sua conta, precisamos de alguns dados obrigatórios.
                </Typography>

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField label="Nome completo *" fullWidth value={fullName}
                                onChange={(e) => setFullName(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Data de nascimento *" type="date" fullWidth
                                InputLabelProps={{ shrink: true }} value={birthDate}
                                onChange={(e) => setBirthDate(e.target.value)} />
                        </Grid>
                        <Grid item xs={8}>
                            <TextField label="CEP *" fullWidth value={cep}
                                onChange={handleCepChange}
                                InputProps={{
                                    endAdornment: loadingCep ? <CircularProgress size={18} /> : null
                                }}
                                placeholder="00000-000" />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField label="Número *" fullWidth value={numero}
                                onChange={(e) => setNumero(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Logradouro" fullWidth value={logradouro}
                                onChange={(e) => setLogradouro(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Complemento" fullWidth value={complemento}
                                onChange={(e) => setComplemento(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Bairro" fullWidth value={bairro}
                                onChange={(e) => setBairro(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField label="Cidade" fullWidth value={cidade}
                                onChange={(e) => setCidade(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField label="UF" fullWidth value={estado}
                                onChange={(e) => setEstado(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <Button type="submit" variant="contained" fullWidth disabled={loading}>
                                Concluir cadastro
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
}
